import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../app/hooks';
import { Mixpanel } from '../../utils/mixpanel';
import { AICA_AUDITOR_COMMENTS_PILL_CLICKED } from '../../utils/constants/mixpanelEvents/investorEvents';
import { fetchVendorSourceMap } from '../../store/user/userV2/actions';
import AuditorCommentsDrawer from '../../modules/Dashboard/DashboardModules/AuditedFinancialsVisualisation/AuditorCommentsDrawer/AuditorCommentsDrawer';
import { AUDITED_FINANCIALS_VENDOR_PROBE } from '../../utils/constants/commonConstants';

/**
 * Availability, tracking and drawer state for the Auditor's Comments action, separated from how it
 * is presented: at full width the headers render it as a pill, and when they compact it becomes an
 * item in their actions menu. Both need the same vendor gating and the same drawer, and a menu item
 * is `{ name, onClick }` with nowhere to hang state.
 *
 * Call this ONCE per surface and render both presentations from that one instance — a pill is
 * `isAvailable && <CtaPill label={AUDITOR_COMMENTS_LABEL} icon={ctaPurpleRight} onClick={open} />`,
 * plus `drawer` somewhere that stays mounted across the compact/full-width switch. A wrapper
 * component that calls this hook for itself would mount a second instance beside the caller's and
 * fire `fetchVendorSourceMap` twice for one pill; that is why the old pill component is gone.
 */
export function useAuditorCommentsAction(orgId: string) {
    const dispatch = useDispatch();
    const [showDrawer, setShowDrawer] = useState(false);
    const user = useAppSelector((state) => state.user);
    const vendorSourceMap = useAppSelector((state) => state.user.vendorSourceMap);
    const auditedVendors: string[] = vendorSourceMap?.AUDITED_FINANCIALS || [];

    useEffect(() => {
        if (!vendorSourceMap && user?.userData?.investorOrgId) {
            dispatch(
                fetchVendorSourceMap({
                    investorOrgId: user.userData.investorOrgId,
                    modules: ['AUDITED_FINANCIALS'],
                }),
            );
        }
    }, [dispatch, vendorSourceMap, user?.userData?.investorOrgId]);

    const open = () => {
        Mixpanel.track(AICA_AUDITOR_COMMENTS_PILL_CLICKED, {
            role: user?.userData?.accountType,
            userId: user?.userData?.userId,
            companyOrgId: orgId,
            investorOrgId: user?.userData?.investorOrgId,
            userEmail: user?.userData?.primaryEmail,
            currentUrl: window.location.href,
        });
        setShowDrawer(true);
    };

    return {
        isAvailable: auditedVendors.includes(AUDITED_FINANCIALS_VENDOR_PROBE) && !!orgId,
        open,
        drawer: showDrawer ? <AuditorCommentsDrawer orgId={orgId} onClose={() => setShowDrawer(false)} /> : null,
    };
}
